/**
 * Correction du géoïde EGM96.
 *
 * Portage de `GeoidCorrector.kt`.
 *
 * Le GPS donne une altitude **ellipsoïdale**, mesurée depuis l'ellipsoïde
 * WGS84, alors qu'une carte, un panneau de col ou un profil d'étape donnent
 * l'altitude **orthométrique**, mesurée depuis le niveau moyen des mers. En
 * France métropolitaine l'écart approche 45 m — assez pour rendre l'affichage
 * inutilisable si on l'ignore.
 *
 * ⚠️ Ne s'applique **qu'aux altitudes GPS**. Celles issues d'un modèle
 * numérique de terrain (OpenTopoData, Open-Meteo) sont déjà orthométriques :
 * les corriger les fausserait une seconde fois.
 */

/** Nombre de lignes de la grille : 90° N à 90° S par pas de 0,25°. */
const ROWS = 721

/** Nombre de colonnes : 0° E à 359,75° E par pas de 0,25°. */
const COLS = 1440

/** Pas de la grille, en degrés (15 minutes d'arc). */
const CELL_SIZE = 0.25

/** Chemin de la grille, relatif à la base de l'application. */
export const GEOID_GRID_URL = './data/egm96_15.bin'

export const GEOID_GRID_BYTES = ROWS * COLS * 2

/**
 * Grille d'ondulations du géoïde.
 *
 * Les valeurs sont stockées en centimètres sur 16 bits signés, ligne par
 * ligne depuis le pôle nord.
 */
export class GeoidGrid {
  constructor(private readonly values: Int16Array) {
    if (values.length !== ROWS * COLS) {
      throw new Error(
        `Grille géoïde de taille inattendue : ${values.length} valeurs au lieu de ${ROWS * COLS}.`,
      )
    }
  }

  /**
   * Construit la grille depuis les octets bruts du fichier.
   *
   * Les entiers sont en **gros-boutien**, alors que toutes les plateformes
   * visées sont petit-boutiennes : l'échange d'octets est fait une fois au
   * chargement plutôt qu'à chaque lecture.
   */
  static fromBuffer(buffer: ArrayBuffer): GeoidGrid {
    if (buffer.byteLength !== GEOID_GRID_BYTES) {
      throw new Error(
        `Fichier géoïde de taille inattendue : ${buffer.byteLength} octets au lieu de ${GEOID_GRID_BYTES}.`,
      )
    }

    const view = new DataView(buffer)
    const values = new Int16Array(ROWS * COLS)

    for (let i = 0; i < values.length; i++) {
      values[i] = view.getInt16(i * 2, false)
    }

    return new GeoidGrid(values)
  }

  /**
   * Ondulation du géoïde en un point, en mètres.
   *
   * Interpolation bilinéaire entre les quatre nœuds encadrant le point.
   */
  undulation(lat: number, lon: number): number {
    // Ramène la longitude dans [0, 360[ : la grille commence au méridien de
    // Greenwich et ne connaît pas les longitudes négatives.
    let lng = lon % 360
    if (lng < 0) lng += 360

    const rowF = (90 - lat) / CELL_SIZE
    const colF = lng / CELL_SIZE

    // La ligne est bornée à l'avant-dernière pour que `r0 + 1` reste valide ;
    // aux pôles, l'interpolation se fait donc dans la cellule de bord.
    const r0 = clamp(Math.floor(rowF), 0, ROWS - 2)

    // La colonne, elle, va jusqu'à la dernière : c'est ce qui rend le
    // rebouclage sur l'antiméridien atteignable. La borner à l'avant-dernière
    // ferait extrapoler sur la dernière cellule au lieu d'interpoler.
    const c0 = clamp(Math.floor(colF), 0, COLS - 1)

    const r1 = r0 + 1
    const c1 = c0 + 1 >= COLS ? 0 : c0 + 1

    const dr = rowF - r0
    const dc = colF - c0

    const v00 = this.values[r0 * COLS + c0]!
    const v01 = this.values[r0 * COLS + c1]!
    const v10 = this.values[r1 * COLS + c0]!
    const v11 = this.values[r1 * COLS + c1]!

    const value =
      v00 * (1 - dr) * (1 - dc) +
      v01 * (1 - dr) * dc +
      v10 * dr * (1 - dc) +
      v11 * dr * dc

    // La grille est en centimètres.
    return value / 100
  }

  /** Altitude au-dessus du niveau moyen des mers, en mètres. */
  toMsl(lat: number, lon: number, ellipsoidalAltitude: number): number {
    return ellipsoidalAltitude - this.undulation(lat, lon)
  }
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value
}

/**
 * Chargeur paresseux de la grille.
 *
 * Le fichier pèse deux mégaoctets : il n'est demandé qu'au premier besoin
 * d'altitude, et tant qu'il n'est pas là, l'altitude brute est renvoyée telle
 * quelle plutôt que de faire attendre l'affichage.
 */
export class GeoidCorrector {
  #grid: GeoidGrid | null = null
  #loading: Promise<GeoidGrid | null> | null = null

  constructor(private readonly url: string = GEOID_GRID_URL) {}

  get isReady(): boolean {
    return this.#grid !== null
  }

  /**
   * Lance le chargement s'il n'a pas déjà eu lieu.
   *
   * Un échec n'est pas propagé : sans grille, l'application affiche l'altitude
   * ellipsoïdale, ce qui vaut mieux que pas d'altitude du tout.
   */
  load(): Promise<GeoidGrid | null> {
    this.#loading ??= fetch(this.url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Grille géoïde indisponible (HTTP ${response.status}).`)
        }
        return response.arrayBuffer()
      })
      .then((buffer) => {
        this.#grid = GeoidGrid.fromBuffer(buffer)
        return this.#grid
      })
      .catch((error: unknown) => {
        console.warn('[VeloGPX] Correction géoïde indisponible', error)
        return null
      })

    return this.#loading
  }

  /**
   * Corrige une altitude GPS si la grille est disponible.
   *
   * Déclenche le chargement au passage : les relevés suivants en profiteront.
   */
  toMsl(lat: number, lon: number, ellipsoidalAltitude: number): number {
    if (this.#grid === null) {
      void this.load()
      return ellipsoidalAltitude
    }

    return this.#grid.toMsl(lat, lon, ellipsoidalAltitude)
  }
}
